import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Plus, Save, Star, Trash2, Rocket, GripVertical, Eye, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { ProgramTemplatesCatalog } from "@/components/admin/ProgramTemplatesCatalog";

/**
 * AdminProgramsManager — manage bootcamp / accelerator style playbooks.
 * A Program is a template of ordered Milestones; enrolling a Company
 * clones them into per-company instances (see routes_program.py).
 *
 * Cards and forms surface both English and Arabic copy so admins can
 * curate the program preview shown on the visitor Journey in both
 * languages without leaving the admin panel.
 */
export function AdminProgramsManager() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/programs");
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await client.post("/admin/programs", { name });
      toast.success("Program created");
      setNewName("");
      setSelected(res.data.program_id);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setCreating(false);
    }
  };

  if (selected) {
    return <ProgramDetail programId={selected} isAr={isAr} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="admin-panel-programs">
      <Card className="rounded-2xl border border-border p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Rocket className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-xl font-semibold">Programs</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Bootcamp-style playbooks. Define an ordered set of milestones, then enroll a Company to clone them as trackable instances. Each program carries both English and Arabic copy so visitors see it in their language.
            </p>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl border border-border p-4">
        <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New program name — e.g. Series A Playbook, Pre-seed Bootcamp"
            className="rounded-xl"
            data-testid="admin-programs-new-name"
          />
          <Button type="submit" className="btn-brand rounded-xl gap-2" disabled={creating || !newName.trim()} data-testid="admin-programs-create">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create program
          </Button>
        </form>
      </Card>

      <ProgramTemplatesCatalog onInstalled={() => load()} />

      {loading ? (
        <div className="flex justify-center py-14"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="admin-programs-empty">
          <Rocket className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No programs yet — create your first playbook above.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <Card
              key={p.program_id}
              onClick={() => setSelected(p.program_id)}
              className="rounded-2xl border border-border p-5 hover:border-primary/40 hover:shadow-md transition-all cursor-pointer"
              data-testid={`admin-programs-card-${p.program_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-display font-semibold truncate">{p.name}</p>
                  {p.name_ar && (
                    <p className="font-display text-sm text-muted-foreground truncate" dir="rtl" data-testid={`admin-programs-card-name-ar-${p.program_id}`}>
                      {p.name_ar}
                    </p>
                  )}
                  {p.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>}
                </div>
                {p.is_default && <Star className="h-4 w-4 text-primary flex-shrink-0" />}
              </div>
              <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                <Badge variant="secondary" className="rounded-full">{p.milestone_count} milestones</Badge>
                <span>·</span>
                <span>{p.enrolled_count} enrolled</span>
                {p.installed_from_template && (
                  <>
                    <span>·</span>
                    <span className="text-primary">preset</span>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Detail — edit metadata + milestones list (English + Arabic)
// ----------------------------------------------------------------------------
function ProgramDetail({ programId, isAr, onBack }) {
  const [program, setProgram] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newTitleAr, setNewTitleAr] = useState("");
  const [newStage, setNewStage] = useState("");

  const load = useCallback(async () => {
    try {
      const [pRes, mRes] = await Promise.all([
        client.get(`/admin/programs`),
        client.get(`/admin/programs/${programId}/milestones`),
      ]);
      const p = (pRes.data?.items || []).find((x) => x.program_id === programId);
      setProgram(p || null);
      setMilestones(mRes.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  }, [programId]);

  useEffect(() => { load(); }, [load]);

  const savePrg = async () => {
    setSaving(true);
    try {
      await client.patch(`/admin/programs/${programId}`, {
        name: program.name,
        name_ar: program.name_ar || "",
        description: program.description,
        description_ar: program.description_ar || "",
        stage_default: program.stage_default,
        is_default: program.is_default,
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const addMilestone = async (e) => {
    e?.preventDefault?.();
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      await client.post(`/admin/programs/${programId}/milestones`, {
        title: newTitle.trim(),
        title_ar: newTitleAr.trim(),
        stage: newStage.trim(),
        order: milestones.length,
      });
      setNewTitle(""); setNewTitleAr(""); setNewStage("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const deleteMs = async (id) => {
    try {
      await client.delete(`/admin/programs/${programId}/milestones/${id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const patchMs = async (id, updates) => {
    try {
      await client.patch(`/admin/programs/${programId}/milestones/${id}`, updates);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  if (!program) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-programs-detail">
      <ProgramLivePreview program={program} milestones={milestones} />
      <Card className="rounded-2xl border border-border p-6 space-y-5">
        <div className="flex items-start gap-2">
          <Button variant="ghost" size="icon" onClick={onBack} className="h-9 w-9 rounded-lg" aria-label="Back" data-testid="admin-programs-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 grid gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">English name</Label>
              <Input value={program.name} onChange={(e) => setProgram({ ...program, name: e.target.value })} className="rounded-xl font-display text-lg mt-1" data-testid="admin-programs-name" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Arabic name</Label>
              <Input dir="rtl" value={program.name_ar || ""} onChange={(e) => setProgram({ ...program, name_ar: e.target.value })} className="rounded-xl font-display text-lg mt-1" placeholder="اسم البرنامج بالعربية" data-testid="admin-programs-name-ar" />
            </div>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Description (EN)</Label>
            <textarea value={program.description || ""} onChange={(e) => setProgram({ ...program, description: e.target.value })} rows={3} className="mt-1 w-full rounded-xl border border-input bg-transparent p-3 text-sm" data-testid="admin-programs-desc" />
          </div>
          <div>
            <Label className="text-xs">Description (AR)</Label>
            <textarea dir="rtl" value={program.description_ar || ""} onChange={(e) => setProgram({ ...program, description_ar: e.target.value })} rows={3} className="mt-1 w-full rounded-xl border border-input bg-transparent p-3 text-sm" placeholder="وصف البرنامج بالعربية" data-testid="admin-programs-desc-ar" />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Default stage</Label>
            <Input value={program.stage_default || ""} onChange={(e) => setProgram({ ...program, stage_default: e.target.value })} className="rounded-xl mt-1" placeholder="e.g. sourced, diligence" />
          </div>
          <label className="flex items-center gap-2 text-sm sm:self-end">
            <input type="checkbox" checked={!!program.is_default} onChange={(e) => setProgram({ ...program, is_default: e.target.checked })} data-testid="admin-programs-default" />
            Use as default for new companies
          </label>
        </div>
        <div className="flex justify-end">
          <Button className="btn-brand rounded-xl gap-2" onClick={savePrg} disabled={saving} data-testid="admin-programs-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save program
          </Button>
        </div>
      </Card>

      <Card className="rounded-2xl border border-border p-6 space-y-4" data-testid="admin-programs-milestones">
        <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">Milestones · English + Arabic</h3>
        <form onSubmit={addMilestone} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_140px_auto] gap-3">
          <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="New milestone (EN)" className="rounded-xl" data-testid="admin-programs-new-milestone" />
          <Input dir="rtl" value={newTitleAr} onChange={(e) => setNewTitleAr(e.target.value)} placeholder="عنوان الخطوة بالعربية" className="rounded-xl" data-testid="admin-programs-new-milestone-ar" />
          <Input value={newStage} onChange={(e) => setNewStage(e.target.value)} placeholder="Stage (optional)" className="rounded-xl" />
          <Button type="submit" className="rounded-xl gap-1" disabled={creating || !newTitle.trim()} data-testid="admin-programs-add-milestone">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add
          </Button>
        </form>
        {milestones.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No milestones yet.</p>
        ) : (
          <ol className="divide-y divide-border">
            {milestones.map((m) => (
              <li key={m.milestone_id} className="flex items-start gap-3 py-3" data-testid={`admin-programs-ms-${m.milestone_id}`}>
                <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-2.5" />
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_1fr_130px] gap-2">
                  <Input
                    defaultValue={m.title}
                    onBlur={(e) => e.target.value !== m.title && patchMs(m.milestone_id, { title: e.target.value })}
                    className="rounded-lg"
                    data-testid={`admin-programs-ms-title-${m.milestone_id}`}
                  />
                  <Input
                    dir="rtl"
                    defaultValue={m.title_ar || ""}
                    placeholder="عنوان بالعربية"
                    onBlur={(e) => e.target.value !== (m.title_ar || "") && patchMs(m.milestone_id, { title_ar: e.target.value })}
                    className="rounded-lg"
                    data-testid={`admin-programs-ms-title-ar-${m.milestone_id}`}
                  />
                  <Input
                    defaultValue={m.stage}
                    placeholder="stage"
                    onBlur={(e) => e.target.value !== m.stage && patchMs(m.milestone_id, { stage: e.target.value })}
                    className="rounded-lg"
                  />
                </div>
                <Button size="icon" variant="ghost" onClick={() => deleteMs(m.milestone_id)} aria-label="Delete milestone" className="mt-1">
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

export default AdminProgramsManager;

// ----------------------------------------------------------------------------
// ProgramLivePreview — mirrors the card visitors see on the Journey page,
// side-by-side in English and Arabic. Updates live as the admin edits the
// program metadata + milestones above.
// ----------------------------------------------------------------------------
function ProgramLivePreview({ program, milestones }) {
  const [lang, setLang] = useState("en");
  const displayed = useMemo(() => {
    const isAr = lang === "ar";
    return {
      name: isAr ? (program.name_ar || program.name) : program.name,
      description: isAr ? (program.description_ar || program.description) : program.description,
      steps: milestones.map((m) => (isAr ? (m.title_ar || m.title) : m.title)),
      hint: isAr ? "معاينة كما يراها الزائر" : "Preview as visitors see it",
      dir: isAr ? "rtl" : "ltr",
    };
  }, [program, milestones, lang]);

  return (
    <Card className="rounded-2xl border border-primary/30 bg-primary/5 p-5 space-y-4" data-testid="admin-programs-preview">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <Eye className="h-4 w-4" />
          </span>
          <p className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">Live preview</p>
        </div>
        <div className="inline-flex rounded-xl border border-border p-0.5" role="tablist" aria-label="Preview language">
          <button
            type="button"
            onClick={() => setLang("en")}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${lang === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="admin-programs-preview-lang-en"
          >
            English
          </button>
          <button
            type="button"
            onClick={() => setLang("ar")}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${lang === "ar" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="admin-programs-preview-lang-ar"
          >
            العربية
          </button>
        </div>
      </div>
      <div dir={displayed.dir} className="rounded-2xl border border-border bg-background p-5" data-testid="admin-programs-preview-card">
        <p className="font-display text-lg font-semibold" data-testid="admin-programs-preview-name">
          {displayed.name || <span className="text-muted-foreground italic">— {lang === "ar" ? "أضف اسمًا بالعربية" : "Add a name"} —</span>}
        </p>
        {displayed.description && (
          <p className="text-xs text-muted-foreground mt-1" data-testid="admin-programs-preview-desc">
            {displayed.description}
          </p>
        )}
        {displayed.steps.length > 0 ? (
          <ol className="mt-3 space-y-1.5">
            {displayed.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-xs" data-testid={`admin-programs-preview-step-${i}`}>
                {s ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" />
                ) : (
                  <Circle className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                )}
                <span className={s ? "" : "text-muted-foreground italic"}>
                  {s || (lang === "ar" ? "— أضف عنوان الخطوة بالعربية —" : "— Milestone title missing —")}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-xs text-muted-foreground mt-3 italic">
            {lang === "ar" ? "لا توجد خطوات بعد." : "No milestones yet."}
          </p>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">{displayed.hint} · {milestones.length} {lang === "ar" ? "خطوة" : "steps"}</p>
    </Card>
  );
}
